import React, { useState, useEffect } from 'react';
import { adminApi } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { Truck } from 'lucide-react';

const ShipModal = ({ onConfirm, onCancel, submitting }) => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('');
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="ship-modal">
      <div className="bg-white max-w-md w-full p-6">
        <h3 className="text-lg font-medium mb-2 flex items-center gap-2">
          <Truck className="w-5 h-5" />
          Spedisci ordine
        </h3>
        <p className="text-sm text-[#57534E] mb-4">
          Il cliente riceverà un'email e una notifica con i dettagli della spedizione.
        </p>
        <label className="block text-xs uppercase tracking-wider text-[#78716C] mb-1">Corriere (facoltativo)</label>
        <input
          type="text"
          value={carrier}
          onChange={(e) => setCarrier(e.target.value)}
          placeholder="Es. BRT, DHL, Poste Italiane..."
          data-testid="ship-carrier-input"
          className="w-full border border-[#E5E0D8] p-3 text-sm mb-3 focus:outline-none focus:border-[#1C1917]"
        />
        <label className="block text-xs uppercase tracking-wider text-[#78716C] mb-1">Numero di tracking (facoltativo)</label>
        <input
          type="text"
          value={trackingNumber}
          onChange={(e) => setTrackingNumber(e.target.value)}
          placeholder="Es. 1234567890 oppure link di tracciamento"
          data-testid="ship-tracking-input"
          className="w-full border border-[#E5E0D8] p-3 text-sm mb-4 focus:outline-none focus:border-[#1C1917]"
        />
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm bg-[#F2EFE9] hover:bg-[#E5E0D8] transition-colors"
            data-testid="ship-cancel-btn"
          >
            Annulla
          </button>
          <button
            onClick={() => onConfirm(trackingNumber, carrier)}
            disabled={submitting}
            className="px-4 py-2 text-sm bg-[#1C1917] text-white hover:bg-[#57534E] transition-colors disabled:opacity-40"
            data-testid="ship-confirm-btn"
          >
            {submitting ? 'Invio...' : 'Conferma spedizione'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function AdminOrdersPage() {
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [shippingOrder, setShippingOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, [filter]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getOrders(filter || undefined);
      setOrders(response.data.orders);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus, extra = {}) => {
    if (newStatus === 'shipped' && !extra.confirmed) {
      setShippingOrder(orderId);
      return;
    }
    setSubmitting(true);
    try {
      await adminApi.updateOrderStatus(orderId, newStatus, {
        tracking_number: extra.tracking_number,
        carrier: extra.carrier,
      });
      toast({ title: newStatus === 'shipped' ? 'Ordine spedito, cliente avvisato via email' : 'Stato aggiornato' });
      setShippingOrder(null);
      fetchOrders();
    } catch (error) {
      toast({ title: 'Errore', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const statuses = [
    { value: '', label: 'Tutti' },
    { value: 'pending', label: 'In attesa' },
    { value: 'confirmed', label: 'Confermati' },
    { value: 'processing', label: 'In lavorazione' },
    { value: 'shipped', label: 'Spediti' },
    { value: 'delivered', label: 'Consegnati' },
    { value: 'cancelled', label: 'Annullati' },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'bg-[#4A5D23] text-white';
      case 'shipped': return 'bg-blue-500 text-white';
      case 'processing': return 'bg-yellow-500 text-white';
      case 'confirmed': return 'bg-green-500 text-white';
      case 'cancelled': return 'bg-[#C05746] text-white';
      default: return 'bg-[#E5E0D8] text-[#57534E]';
    }
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <h2 className="text-2xl font-light">Gestione Ordini</h2>
        <div className="flex flex-wrap gap-2">
          {statuses.map((status) => (
            <button
              key={status.value}
              onClick={() => setFilter(status.value)}
              data-testid={`filter-${status.value || 'all'}`}
              className={`px-4 py-2 text-sm transition-colors ${
                filter === status.value
                  ? 'bg-[#1C1917] text-white'
                  : 'bg-[#F2EFE9] hover:bg-[#E5E0D8]'
              }`}
            >
              {status.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-[#E5E0D8] animate-pulse" />
          ))}
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16 bg-[#F2EFE9]">
          <p className="text-[#57534E]">Nessun ordine trovato</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order, idx) => (
            <div key={order.order_id} className="bg-[#F2EFE9] p-6" data-testid={`admin-order-${idx}`}>
              <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
                <div>
                  <p className="font-mono text-sm text-[#57534E]">{order.order_id}</p>
                  <p className="font-medium mt-1">
                    {new Date(order.created_at).toLocaleDateString('it-IT', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                  <p className="text-sm text-[#57534E] mt-1">
                    Cliente: {order.customer_name || order.user_id}
                    {order.customer_email && <span className="block text-xs">{order.customer_email}</span>}
                  </p>
                  {order.tracking_number && (
                    <p className="text-sm mt-1" data-testid={`order-tracking-${idx}`}>
                      <span className="text-[#78716C]">Tracking: </span>
                      <span className="font-mono">{order.tracking_number}</span>
                      {order.carrier && <span className="text-[#78716C]"> ({order.carrier})</span>}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-2xl font-light">€{order.total?.toFixed(2)}</p>
                  <p className={`inline-block px-3 py-1 text-sm mt-2 ${
                    order.payment_status === 'paid' ? 'bg-[#4A5D23] text-white' : 'bg-[#E5E0D8]'
                  }`}>
                    {order.payment_status === 'paid' ? 'Pagato' : 'Non pagato'}
                  </p>
                </div>
              </div>

              <div className="flex gap-4 overflow-x-auto pb-2 mb-4">
                {order.items?.map((item, i) => (
                  <div key={i} className="shrink-0 flex gap-2 bg-white p-2">
                    <img
                      src={item.product?.images?.[0] || 'https://via.placeholder.com/40x50'}
                      alt={item.name}
                      className="w-10 h-12 object-cover"
                    />
                    <div className="text-sm">
                      <p className="font-medium">{item.name}</p>
                      <p className="text-[#57534E]">{item.size} / {item.color} x{item.quantity}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-[#E5E0D8]">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#57534E]">Stato:</span>
                  <span className={`px-3 py-1 text-sm ${getStatusColor(order.status)}`}>
                    {statuses.find(s => s.value === order.status)?.label || order.status}
                  </span>
                </div>
                <select
                  value={order.status}
                  onChange={(e) => handleStatusChange(order.order_id, e.target.value)}
                  data-testid={`order-status-select-${idx}`}
                  className="px-3 py-2 border border-[#E5E0D8] bg-white"
                >
                  {statuses.filter(s => s.value).map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}

      {shippingOrder && (
        <ShipModal
          submitting={submitting}
          onConfirm={(tracking_number, carrier) =>
            handleStatusChange(shippingOrder, 'shipped', { confirmed: true, tracking_number, carrier })
          }
          onCancel={() => setShippingOrder(null)}
        />
      )}
    </div>
  );
}
